import { EquityDataPoint, TradeData, PortfolioSummaryData, RiskMetric, AllocationDataPoint, MonthlyPerformanceData } from './types';
import { parseISO, startOfMonth, endOfMonth, format, subMonths, getMonth, getYear, subDays } from 'date-fns';
import * as mockData from './mockData'; // Import mock data generators for fallbacks

// Helper function for random values in range (needed when using mock data)
const random = (min: number, max: number): number => Math.random() * (max - min) + min;

const RISK_FREE_RATE = 0.02; // Assume a 2% annual risk-free rate for Sharpe

export const calculateEquityCurve = (trades: TradeData[], initialCapital: number): EquityDataPoint[] => {
  if (!trades || trades.length === 0) {
    return [{ date: new Date().toISOString().split('T')[0], value: initialCapital }];
  }

  trades.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

  const equityCurve: EquityDataPoint[] = [{ date: trades[0].timestamp, value: initialCapital }];
  let currentCapital = initialCapital;
  const positions: { [symbol: string]: { quantity: number; avgCost: number } } = {};

  trades.forEach(trade => {
    const tradeValue = trade.quantity * trade.price;
    const symbol = trade.symbol;

    if (trade.action === 'buy') {
      currentCapital -= tradeValue;
      const currentPosition = positions[symbol] || { quantity: 0, avgCost: 0 };
      const totalCost = currentPosition.avgCost * currentPosition.quantity + tradeValue;
      currentPosition.quantity += trade.quantity;
      currentPosition.avgCost = currentPosition.quantity === 0 ? 0 : totalCost / currentPosition.quantity;
      positions[symbol] = currentPosition;
    } else if (trade.action === 'sell') {
      currentCapital += tradeValue;
      const currentPosition = positions[symbol];
      if (currentPosition) {
        // Realized P/L could be calculated here if needed, but equity curve focuses on cash + position value
        currentPosition.quantity -= trade.quantity;
        if (currentPosition.quantity <= 0) {
          delete positions[symbol]; // Close position if quantity is zero or less
        } else {
          positions[symbol] = currentPosition;
        }
      }
    }

    // Estimate portfolio value (Cash + Market Value of Positions)
    // This requires current market prices, which we don't have just from trades.
    // Simplification: Use the last trade price as a proxy for current value for simplicity
    let positionValue = 0;
    Object.values(positions).forEach(pos => {
        const lastTradeForSymbol = trades.slice().reverse().find(t => t.symbol === Object.keys(positions).find(key => positions[key] === pos));
        positionValue += pos.quantity * (lastTradeForSymbol?.price || pos.avgCost); // Use last price or avg cost
    });

    const portfolioValue = currentCapital + positionValue;

    // Add data point, ensuring date is unique or handling appropriately
    const dateStr = format(parseISO(trade.timestamp), 'yyyy-MM-dd');
    const lastPoint = equityCurve[equityCurve.length - 1];
    if(lastPoint.date === dateStr) {
        lastPoint.value = parseFloat(portfolioValue.toFixed(2)); // Update last point for same day
    } else {
        equityCurve.push({ date: dateStr, value: parseFloat(portfolioValue.toFixed(2)) });
    }
  });

  // Fill potential gaps if needed (simplified: just return calculated points)
  return equityCurve;
};

export const calculatePerformanceMetrics = (equityCurve: EquityDataPoint[]): { totalReturn: number; ytdReturn: number; monthlyReturn: number } => {
  if (!equityCurve || equityCurve.length < 2) {
    return { totalReturn: 0, ytdReturn: 0, monthlyReturn: 0 };
  }

  const startValue = equityCurve[0].value;
  const endValue = equityCurve[equityCurve.length - 1].value;
  const totalReturn = ((endValue / startValue) - 1) * 100;

  // YTD calculation needs proper date handling
  const today = new Date();
  const startOfYear = new Date(today.getFullYear(), 0, 1);
  const startOfYearStr = format(startOfYear, 'yyyy-MM-dd');
  const firstPointOfYear = equityCurve.find(p => p.date >= startOfYearStr) || equityCurve[0];
  const ytdReturn = ((endValue / firstPointOfYear.value) - 1) * 100;

  // Monthly calculation
  const startOfLastMonth = startOfMonth(subMonths(today, 1));
  const endOfLastMonth = endOfMonth(subMonths(today, 1));
  const startOfMonthStr = format(startOfLastMonth, 'yyyy-MM-dd');
  const endOfMonthStr = format(endOfLastMonth, 'yyyy-MM-dd');

  const firstPointOfMonth = equityCurve.find(p => p.date >= startOfMonthStr) || null;
   // Find the last point *on or before* the end of the month
  const lastPointOfMonth = equityCurve.slice().reverse().find(p => p.date <= endOfMonthStr) || null;

  let monthlyReturn = 0;
  if (firstPointOfMonth && lastPointOfMonth) {
      monthlyReturn = ((lastPointOfMonth.value / firstPointOfMonth.value) - 1) * 100;
  }


  return {
    totalReturn: parseFloat(totalReturn.toFixed(2)),
    ytdReturn: parseFloat(ytdReturn.toFixed(2)),
    monthlyReturn: parseFloat(monthlyReturn.toFixed(2)),
  };
};

export const calculateRiskMetrics = (equityCurve: EquityDataPoint[], trades: TradeData[]): RiskMetric[] => {
    if (!equityCurve || equityCurve.length < 2) return mockData.generateRiskMetrics(); // Fallback to mock if insufficient data

    let peak = equityCurve[0].value;
    let maxDrawdown = 0;
    const returns: number[] = [];

    for (let i = 1; i < equityCurve.length; i++) {
        const dailyReturn = (equityCurve[i].value / equityCurve[i-1].value) - 1;
        returns.push(dailyReturn);
        peak = Math.max(peak, equityCurve[i].value);
        const drawdown = (equityCurve[i].value / peak) - 1;
        maxDrawdown = Math.min(maxDrawdown, drawdown);
    }

    const avgReturn = returns.reduce((sum, r) => sum + r, 0) / returns.length;
    const stdDev = Math.sqrt(returns.reduce((sumSq, r) => sumSq + Math.pow(r - avgReturn, 2), 0) / returns.length);
    const annualizedVolatility = stdDev * Math.sqrt(252); // Assuming 252 trading days

    // Simplified Sharpe: (Avg Daily Return * 252 - RiskFree) / Annualized Volatility
    const annualizedReturn = avgReturn * 252;
    const sharpeRatio = annualizedVolatility === 0 ? 0 : (annualizedReturn - RISK_FREE_RATE) / annualizedVolatility;

    // Value at Risk (simplified percentile method)
    const sortedReturns = [...returns].sort((a, b) => a - b);
    const varIndex = Math.floor(0.05 * sortedReturns.length); // 5th percentile
    const valueAtRisk = sortedReturns[varIndex] * 100; // As percentage

    // Calculate Exposure (Simplification: assume fully invested if trades exist)
    const exposure = trades.length > 0 ? 100 : 0;

     const getLevel = (value: number, thresholds: [number, number], higherIsWorse: boolean = true): 'low' | 'medium' | 'high' => {
        if (higherIsWorse) {
            if (value <= thresholds[0]) return 'low';
            if (value <= thresholds[1]) return 'medium';
            return 'high';
        } else { // Higher is better
            if (value >= thresholds[1]) return 'low'; // Low risk level for high score
            if (value >= thresholds[0]) return 'medium';
            return 'high'; // High risk level for low score
        }
    };


    return [
        { id: 'drawdown', name: 'Max Drawdown', value: `${(maxDrawdown * 100).toFixed(1)}%`, level: getLevel(Math.abs(maxDrawdown * 100), [10, 20]), description: 'Maximum peak-to-trough decline.' },
        { id: 'volatility', name: 'Volatility (Ann.)', value: `${(annualizedVolatility * 100).toFixed(1)}%`, level: getLevel(annualizedVolatility * 100, [15, 25]), description: 'Annualized standard deviation of returns.' },
        { id: 'sharpe', name: 'Sharpe Ratio', value: sharpeRatio.toFixed(2), level: getLevel(sharpeRatio, [1.0, 1.5], false), description: 'Risk-adjusted return.' },
        { id: 'exposure', name: 'Net Exposure', value: `${exposure.toFixed(0)}%`, level: getLevel(exposure, [70, 100]), description: 'Market exposure relative to value.' },
        { id: 'var', name: 'Value-at-Risk (VaR)', value: `${valueAtRisk.toFixed(1)}%`, level: getLevel(Math.abs(valueAtRisk), [1.5, 2.5]), description: 'Estimated max loss (95% conf, 1d).' },
    ];
};

export const calculateAllocation = (trades: TradeData[], currentPortfolioValue: number): AllocationDataPoint[] => {
  // Simplistic allocation based on symbols traded. A real calculation needs current holdings and prices.
  if (!trades || trades.length === 0) return [];

  const tradedSymbols = [...new Set(trades.map(t => t.symbol))];
  const allocation: AllocationDataPoint[] = [];
  const totalSymbols = tradedSymbols.length;
  const basePercent = 100 / totalSymbols;

  tradedSymbols.forEach((symbol, index) => {
    // Assign to mock categories/colors for demo
    const typeIndex = index % mockData.ALLOCATION_TYPES.length;
    allocation.push({
      name: symbol, // Use symbol name for simplicity
      value: parseFloat(basePercent.toFixed(1)), // Distribute equally for now
      color: mockData.ALLOCATION_TYPES[typeIndex].color,
    });
  });

   // Adjust last item to ensure sum is 100%
   const currentSum = allocation.reduce((sum, item) => sum + item.value, 0);
   if (allocation.length > 0 && currentSum !== 100) {
       allocation[allocation.length - 1].value = parseFloat((allocation[allocation.length - 1].value + (100 - currentSum)).toFixed(1));
   }


  return allocation;
};

export const calculateMonthlyReview = (trades: TradeData[], equityCurve: EquityDataPoint[]): MonthlyPerformanceData | null => {
     if (!trades || trades.length === 0 || !equityCurve || equityCurve.length < 2) return null;

    const today = new Date();
    const lastMonthDate = subMonths(today, 1);
    const startOfLastMonth = startOfMonth(lastMonthDate);
    const endOfLastMonth = endOfMonth(lastMonthDate);
    const monthName = format(startOfLastMonth, 'MMMM yyyy');

    const startOfMonthStr = format(startOfLastMonth, 'yyyy-MM-dd');
    const endOfMonthStr = format(endOfLastMonth, 'yyyy-MM-dd');

    const firstPointOfMonth = equityCurve.find(p => p.date >= startOfMonthStr);
    const lastPointOfMonth = equityCurve.slice().reverse().find(p => p.date <= endOfMonthStr);


    if (!firstPointOfMonth || !lastPointOfMonth) return null; // Not enough data for the month

    const returnPercent = ((lastPointOfMonth.value / firstPointOfMonth.value) - 1) * 100;

    // Simulate benchmark and contributors
    const benchmarkReturnPercent = returnPercent + random(-2, 2);
    const contributors = [...new Set(trades.map(t => t.symbol))].sort(() => 0.5 - Math.random());

    return {
        month: monthName,
        returnPercent: parseFloat(returnPercent.toFixed(2)),
        benchmarkReturnPercent: parseFloat(benchmarkReturnPercent.toFixed(2)),
        topContributor: contributors[0] || 'N/A',
        bottomContributor: contributors[1] || 'N/A',
        keyEvents: [`Performance influenced by sector trends.`, `Volatility was moderate during ${monthName}.`],
        aiCommentary: `Stewie analysis for ${monthName}: The portfolio showed ${returnPercent >= 0 ? 'positive' : 'negative'} returns, ${returnPercent > benchmarkReturnPercent ? 'outperforming' : 'underperforming'} the benchmark. Consider reviewing allocation.`
    };
};

// Combined function to process trades and return all necessary data details
export const processExternalStrategyTrades = (trades: TradeData[], initialCapital: number): Partial<PortfolioSummaryData & { riskMetrics: RiskMetric[], monthlyPerformance: MonthlyPerformanceData | null, recentTrades: TradeData[] }> => {
    try {
        const equityCurve = calculateEquityCurve(trades, initialCapital);
        if (equityCurve.length < 2) {
             console.warn("Not enough data points in equity curve to calculate metrics.");
             // Return minimal data or fallback mocks
             return {
                totalValue: initialCapital,
                todayChangeValue: 0,
                todayChangePercent: 0,
                equityCurve: [{date: new Date().toISOString().split('T')[0], value: initialCapital}],
                allocation: [],
                riskMetrics: [],
                monthlyPerformance: null,
                recentTrades: trades.slice(0, 8) // Show raw trades if calculations fail
            };
        }
        const performance = calculatePerformanceMetrics(equityCurve);
        const riskMetrics = calculateRiskMetrics(equityCurve, trades);
        const allocation = calculateAllocation(trades, equityCurve[equityCurve.length - 1].value);
        const monthlyPerformance = calculateMonthlyReview(trades, equityCurve);
        const recentTrades = trades.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()).slice(0, 8);

        return {
            totalValue: equityCurve[equityCurve.length - 1].value,
            todayChangeValue: equityCurve[equityCurve.length - 1].value - (equityCurve[equityCurve.length - 2]?.value || equityCurve[equityCurve.length - 1].value),
            todayChangePercent: performance.totalReturn, // Using totalReturn as proxy for today if curve is short
            monthlyChangePercent: performance.monthlyReturn,
            ytdChangePercent: performance.ytdReturn,
            equityCurve,
            allocation,
            riskMetrics,
            monthlyPerformance,
            recentTrades,
        };
    } catch (error) {
        console.error("Error processing external strategy trades:", error);
        // Return structure with empty/default values on error
         return {
            totalValue: initialCapital, todayChangeValue: 0, todayChangePercent: 0,
            equityCurve: [{date: new Date().toISOString().split('T')[0], value: initialCapital}],
            allocation: [], riskMetrics: [], monthlyPerformance: null, recentTrades: []
        };
    }
};